import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Sparkles, Activity, PlugZap, Plug } from "lucide-react";
import { useSchema } from "@/context/SchemaContext";
import { api, fmtNum, fmtDate } from "@/lib/api";
import { Icon } from "@/lib/icons";
import ResourceTable from "@/components/resources/ResourceTable";
import HostingStudio from "@/components/resources/HostingStudio";
import ChatPanel from "@/components/chat/ChatPanel";

const FACTORY_PROMPTS = {
  f1: ["سجّل نطاق جديد واربطه بخادم Hetzner في هلسنكي", "أضف سجل A لنطاق aql4.com يشير إلى 1.2.3.4 مع تفعيل Cloudflare Proxy", "اعرض كل الخوادم وحالتها وتكلفتها الشهرية"],
  f2: ["أنشئ محفظة ريال سعودي عبر Moyasar بآيبان افتراضي", "سجّل معاملة واردة 12,500 ريال من شركة الأفق مقابل ترخيص", "أصدر فاتورة إلكترونية ZATCA بقيمة 8,000 ريال للعميل أحمد"],
  f3: ["اكتب منشوراً إعلانياً لمنتج جديد وجدوله على تيك توك وسناب الساعة 8 مساءً", "أنشئ حملة Meta Ads بميزانية 3,000 ريال", "اربط حساب إنستغرام @aql4 وفعّل الردود التلقائية"],
  f4: ["ابنِ وكيل خدمة عملاء بالعربية لمتجر عطور ودرّبه", "ولّد مصنعاً فرعياً لإدارة المطاعم مع وحداته", "ابنِ تطبيق موبايل React Native لحجز المواعيد"],
  f5: ["أضف مورد إلكترونيات من Alibaba بتقييم 4.8", "أنشئ متجر aql4.store بالريال السعودي", "سجّل طلب جملة 500 وحدة وشحنة عبر Aramex"],
  f6: ["ولّد شركة اتصالات عربية بخدمات أرقام وواتساب وصوت", "أنشئ سنترالاً مع رقم افتراضي وتوجيه مكالمات", "ابنِ نموذج اشتراك إنترنت ونموذج شراء وبيع خدمة"],
  f7: ["افهرس المكتبات المثبتة في بيئتي وصنفها حسب الذكاء والصورة والفيديو والصوت", "افحص توافق مكتبات الصور والفيديو والصوت قبل اعتمادها", "سجل NVIDIA NIM كتكامل اختياري واشرح خطوات تفعيله دون ادعاء اتصاله"],
  "aql4.date": ["جدول خطة موعد رئيسي لعرض تجريبي يوم الأحد الساعة 7 مساءً بالتوقيت العربي", "أنشئ قاعة اجتماعات لمدة 45 دقيقة مع ربطها بمسؤول واحد وحالة جاهزية", "اعرض كل خطط التاريخ المجدولة لهذا الأسبوع ورتّبها حسب الأولوية"],
  "factory": ["أنشئ خط إنتاج جديد بقدرة 600 وحدة في الورشة المسائية", "عرض طاقم التشغيل الحالي وحالة كل خط", "سجّل مهمة جديدة لصيانة آلة ذات أولوية عالية"],
  "f8": ["اربط نقطة اتصال Cloudflare الرسمية عبر https://dash.cloudflare.com/c629d703fd697a495e8e8519ed9a7b06/home", "اعرض جميع المناطق والعملات والزوايا في الحساب وصحّة كل Worker", "أنشئ مشروع Pages جديد ونسّقه مع فرع الإنتاج"],
  "f9": ["أنشئ مخططات مستقلة للمنتجات المرجعية: https://app.emergent.sh ، https://vscode-3c2c488c-bb7e-4bf6-95e5-8b879a6769ea.preview.emergentagent.com/?folder=/app ، https://dash.cloudflare.com/c629d703fd697a495e8e8519ed9a7b06/home ، https://aql4.com/ . وثّق القدرات العامة وخطة بناء أصلية وأساس الإذن دون نسخ الشفرة أو البيانات الخاصة.", "جهّز مشروع Full Stack جديدًا واربطه بمستودع ومساحة شفرة وخط بناء واختبارات", "راجع مفاتيح GitHub وCloudflare المتاحة، ثم جهّز خطة نشر Pages دون الادعاء بالتنفيذ إذا لم تكن المفاتيح مربوطة"],
  "f10": ["اعرض حالة AQL Sovereign Bridge وسجل آخر المصافحات والمهام والنتائج", "سجّل نتيجة المهمة ext-001 في سجل مزامنة الجسر", "راجع قدرات النظير واربط فقط فهارس الموارد المصرح بها"],
};

export default function FactoryPage() {
  const { fid } = useParams();
  const { factories, resources } = useSchema();
  const f = factories[fid];
  const [tab, setTab] = useState("ai");
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState({});
  const [cloudflareStatus, setCloudflareStatus] = useState(null);
  const [activity, setActivity] = useState([]);
  const [conv, setConv] = useState(null);

  const mine = useMemo(() => Object.entries(resources).filter(([, s]) => s.factory === fid), [resources, fid]);

  useEffect(() => {
    setTab("ai"); setConv(null);
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/vault/status").then((r) => setStatus(r.data)).catch(() => {});
    if (fid === "f8") {
      api.get("/vault/cloudflare/status").then((r) => setCloudflareStatus(r.data)).catch(() => setCloudflareStatus({ connected: false, message: "غير متوفر" }));
    }
    api.get("/activity", { params: { factory: fid, limit: 30 } }).then((r) => setActivity(r.data)).catch(() => {});
  }, [fid]);

  if (!f) return <div className="p-10 text-slate-400">جارٍ التحميل...</div>;
  const color = f.color;
  const kpis = [
    ...(fid === "f2" ? [{ label: "إجمالي الأرصدة", value: fmtNum(stats?.finance?.balance) }, { label: "الوارد", value: fmtNum(stats?.finance?.inflow) }, { label: "الصادر", value: fmtNum(stats?.finance?.outflow) }] : []),
    ...(fid === "f3" ? [{ label: "إنفاق الإعلانات", value: fmtNum(stats?.ads?.spend) }, { label: "الإيراد", value: fmtNum(stats?.ads?.revenue) }, { label: "ROI", value: `${fmtNum(stats?.ads?.roi, 1)}%` }] : []),
    ...(fid === "f1" ? [{ label: "التكلفة الشهرية للخوادم", value: `$${fmtNum(stats?.infra?.monthly_cost)}` }] : []),
    ...(fid === "f8" ? [{ label: "Cloudflare Status", value: cloudflareStatus?.connected ? "متصل" : "غير متصل" }, { label: "المنطقة", value: cloudflareStatus?.zones?.length ?? 0 }, { label: "Workers", value: cloudflareStatus?.workers?.length ?? 0 }] : []),
    ...mine.map(([r, s]) => ({ label: s.name, value: fmtNum(stats?.counts?.[r]) })),
  ].slice(0, 6);

  return (
    <div className="h-full flex flex-col" data-testid={`factory-page-${fid}`}>
      <div className="relative px-4 sm:px-8 pt-6 pb-4 border-b border-white/[0.06] overflow-hidden">
        <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full blur-3xl opacity-20 pointer-events-none" style={{ background: color }} />
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl grid place-items-center border" style={{ color, background: `${color}14`, borderColor: `${color}44`, boxShadow: `0 0 40px -10px ${color}` }}>
              <Icon name={f.icon} size={26} />
            </div>
            <div>
              <p className="text-[11px] tracking-wide text-slate-500">مصنع {fid.replace("f", "")}</p>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-50">{f.name}</h1>
              <p className="text-sm text-slate-400 mt-1">{f.desc}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 max-w-md" data-testid="factory-integrations">
            {f.integrations?.map((s) => (
              <Link key={s} to="/admin/vault" data-testid={`integration-chip-${s}`} title={status[s] ? "مربوط" : "غير مربوط — اضغط للربط"}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-mono transition-colors ${status[s] ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-white/10 bg-white/[0.03] text-slate-500 hover:text-slate-300"}`}>
                {status[s] ? <PlugZap size={10} /> : <Plug size={10} />}{s.replace(/_(API_)?(KEY|TOKEN|SECRET_KEY|CERTIFICATE|SERVICE_KEY|APP_KEY|APP_TOKEN|ACCESS_TOKEN)$/i, "").replace(/_/g, " ")}
              </Link>
            ))}
          </div>
        </div>
        <div className="relative mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="factory-kpis">
          {kpis.map((k, i) => (
            <div key={i} className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5 fade-up" style={{ animationDelay: `${i * 60}ms` }}>
              <p className="text-[11px] text-slate-500 truncate">{k.label}</p>
              <p className="font-mono text-lg text-slate-100 mt-0.5">{k.value}</p>
            </div>
          ))}
        </div>
        <div className="relative mt-4 flex gap-1 overflow-x-auto pb-1" data-testid="factory-tabs">
          <TabBtn active={tab === "ai"} onClick={() => setTab("ai")} color={color} tid="factory-tab-ai"><Sparkles size={14} /> الذكاء المدمج</TabBtn>
          {fid === "f1" && <TabBtn active={tab === "hosting"} onClick={() => setTab("hosting")} color={color} tid="factory-tab-hosting"><Icon name="Globe" size={14} /> الاستضافة والنشر</TabBtn>}
          {mine.map(([r, s]) => <TabBtn key={r} active={tab === r} onClick={() => setTab(r)} color={color} tid={`factory-tab-${r}`}><Icon name={s.icon} size={14} /> {s.name}</TabBtn>)}
          <TabBtn active={tab === "activity"} onClick={() => setTab("activity")} color={color} tid="factory-tab-activity"><Activity size={14} /> النشاط</TabBtn>
        </div>
      </div>

      <div className="flex-1 min-h-0">
        {tab === "ai" && <ChatPanel scope={fid} color={color} conversationId={conv} onConversation={setConv} prompts={FACTORY_PROMPTS[fid] || []} compact />}
        {tab === "hosting" && fid === "f1" && <HostingStudio color={color} />}
        {fid === "f8" && tab === "ai" && cloudflareStatus && (
          <div className="p-4 sm:p-8">
            <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="flex items-center justify-between gap-3 pb-2">
                <h3 className="text-lg font-semibold text-slate-50">حالة اتصال Cloudflare</h3>
                <span className={`rounded-full px-2 py-1 text-[10px] ${cloudflareStatus.connected ? "bg-emerald-500/10 text-emerald-300" : "bg-amber-500/10 text-amber-300"}`}>
                  {cloudflareStatus.connected ? "متصل" : "غير متصل"}
                </span>
              </div>
              <p className="text-sm text-slate-400">{cloudflareStatus.message || "لا توجد رسالة"}</p>
              <div className="mt-3 grid gap-3 md:grid-cols-3">
                <div className="rounded-xl border border-white/[0.06] bg-[#0A1120] p-3"><p className="text-[11px] text-slate-500">حساب</p><p className="mt-1 text-sm text-slate-100">{cloudflareStatus.account_id || "غير محدد"}</p></div>
                <div className="rounded-xl border border-white/[0.06] bg-[#0A1120] p-3"><p className="text-[11px] text-slate-500">المناطق</p><p className="mt-1 text-sm text-slate-100">{cloudflareStatus.zones?.length ?? 0}</p></div>
                <div className="rounded-xl border border-white/[0.06] bg-[#0A1120] p-3"><p className="text-[11px] text-slate-500">Workers</p><p className="mt-1 text-sm text-slate-100">{cloudflareStatus.workers?.length ?? 0}</p></div>
              </div>
            </div>
          </div>
        )}
        {resources[tab] && <div className="p-4 sm:p-8"><ResourceTable resource={tab} spec={resources[tab]} color={color} /></div>}
        {tab === "activity" && (
          <div className="p-4 sm:p-8 space-y-2" data-testid="factory-activity">
            {activity.length === 0 && <p className="text-slate-500 text-sm text-center py-10">لا نشاط مسجل بعد</p>}
            {activity.map((a) => (
              <div key={a.id} className="panel px-4 py-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`text-[10px] font-mono rounded px-1.5 py-0.5 ${a.by === "agent" ? "bg-cyan-400/10 text-cyan-300" : "bg-white/5 text-slate-400"}`}>{a.by === "agent" ? "الوكيل" : "المالك"}</span>
                  <span className="text-sm text-slate-200 truncate">{a.summary}</span>
                </div>
                <span className="text-[11px] text-slate-500 whitespace-nowrap">{fmtDate(a.at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, color, children, tid }) {
  return (
    <button data-testid={tid} onClick={onClick} className={`shrink-0 h-9 px-3.5 rounded-lg text-xs flex items-center gap-1.5 border transition-colors ${active ? "text-slate-950 font-semibold" : "border-white/[0.08] text-slate-300 hover:bg-white/5"}`}
      style={active ? { background: color, borderColor: color } : {}}>
      {children}
    </button>
  );
}
